import { MetricalpReactProvider } from '@metricalp/react'
import { Box, CssBaseline, ThemeProvider } from '@mui/material'
import { FC, ReactNode, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Route, Routes, useMatch } from 'react-router-dom'
import { AuthService } from './interactors/services/AuthService'
import { useGlobalLoaderStore } from './store/GlobalLoaderStore'
import { LoginPage } from './views/Login/LoginPage'
import { PATHS } from './views/PATHS'
import { CircularProgressPanel } from './views/common/CircularProgressPanel'
import { theme } from './views/theme'
import { PrivateRoutes } from './views/utils/PrivateRoutes'

import { Navigate } from 'react-router-dom'
import { useAccountsStore } from './store/AccountsStore'
import { findHome } from './utils/findHomeForUser'
import { useFirebaseMessaging } from './utils/hooks/useFirebaseMessaging'
import { AdoptionDetailsScreen } from './views/Adoptions/AdoptionDetailsScreen'
import { AdoptionsListScreen } from './views/Adoptions/AdoptionsListScreen'
import { SendMyBeReal } from './views/AnimalDailyPics/SendMyBeReal'
import { TikTokView } from './views/AnimalDailyPics/TikTokView'
import { AddAnimalDocumentScreen } from './views/Animals/AddAnimalDocumentScreen'
import { AddAnimalObservationScreen } from './views/Animals/AddAnimalObservationScreen'
import { AddAnimalOperationScreen } from './views/Animals/AddAnimalOperationScreen'
import { AddAnimalScreen } from './views/Animals/AddAnimalScreen'
import { AnimalDetailsScreen } from './views/Animals/AnimalDetailsScreen'
import { AnimalsListScreen } from './views/Animals/AnimalsListScreen'
import { ObservationDetailsScreen } from './views/Animals/ObservationDetailsScreen'
import { OperationDetailsScreen } from './views/Animals/OperationDetailsScreen'
import { ConversationsScreen } from './views/Chat/ConversationsScreen'
import { GuestAdoptionPage } from './views/GuestAdoption/GuestAdoptionPage'
import { AdoptionFormScreen } from './views/Dashboard/AdoptionFormScreen'
import { ProfilScreen } from './views/Dashboard/ProfilScreen'
import { ProfileDetailsScreen } from './views/Dashboard/ProfileDetailsScreen'
import AuthRedirect from './views/Login/AuthRedirect'
import NotificationModal from './views/NotificationsModal'
import { PetParentsListScreen } from './views/PetParents/PetParentsListScreen'
import { CreatePasswordPage } from './views/Register/CreatePasswordPage'
import { RequestResetPasswordPage } from './views/ResetPassword/RequestResetPasswordPage'
import { ResetPasswordPage } from './views/ResetPassword/ResetPasswordPage'
import { ServiceLocationScreen } from './views/ServicesMap/ServiceLocationScreen'
import { ServicesMapScreen } from './views/ServicesMap/ServicesMapScreen'
import { BrandPage } from './views/Shopping/BrandPage'
import { ProductPage } from './views/Shopping/ProductPage'
import { ShoppingBasket } from './views/Shopping/ShoppingBasket'
import { ShoppingTabs } from './views/Shopping/ShoppingTabs'
import { WelcomePack } from './views/Shopping/WelcomePack'
import { AnimalDetails } from './views/SuiviVeto/AnimalDetails'
import { Layout } from './views/SuiviVeto/Layout'
import { SuiviObservationDetailsScreen } from './views/SuiviVeto/ObservationDetailsScreen'
import { SuiviOperationDetailsScreen } from './views/SuiviVeto/OperationDetailsScreen'
import { GlobalSnackBar } from './views/common/GlobalSnackbar'
import { DashboardDisplay } from './views/utils/DashboardDisplay'
import { Tutorial } from './views/Tutorial/Tutorial'
import { AdoptedTutorial } from './views/TutorialAdoptionDone/Tutorial'
const queryClient = new QueryClient()

interface AnalyticsProviderProps {
  account?: { id: string; firstName: string; lastName: string }
  children: ReactNode
}

const AnalyticsProvider: FC<AnalyticsProviderProps> = ({ account, children }) => {
  const isGuestAdoptionPage = useMatch(PATHS.guestAdoption.absolute) !== null

  // The guest adoption link carries a personal secret in its path, and the analytics script reports every
  // visited path: it is not loaded on that page.
  if (isGuestAdoptionPage) {
    return <>{children}</>
  }

  return (
    <MetricalpReactProvider
      initialSharedCustomProps={{
        screen_view: {
          adopterId: account?.id ?? 'not-connected',
          adopterName: account?.firstName ? `${account?.firstName} ${account?.lastName}` : 'not-connected',
        },
      }}
      tid="mam84"
    >
      {children}
    </MetricalpReactProvider>
  )
}

const App: FC = () => {
  const membersStore = useAccountsStore()
  const { isLoading, setIsLoading } = useGlobalLoaderStore()

  useFirebaseMessaging()

  const getAccountInfoFromLocalStorage = (): { id: string; firstName: string; lastName: string } => {
    const account = localStorage.getItem('connectedAccount')
    return JSON.parse(account ?? '{}')
  }

  const account = getAccountInfoFromLocalStorage()

  useEffect(() => {
    AuthService.tryLogInWithLocalToken()
      .then((account) => {
        if (account) {
          membersStore.changeConnectedAccount(account)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  /*   useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    setVh() // Set on initial load

    window.addEventListener('resize', setVh) // Set on resize

    // Cleanup function
    return () => {
      window.removeEventListener('resize', setVh) // Remove event listener on unmount
    }
  }, []) // Empty dependency array so effect only runs on mount and unmount */

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CircularProgressPanel />
      </ThemeProvider>
    )
  }

  return (
    <>
      <AnalyticsProvider account={account}>
        <CssBaseline />

        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '100%',
                overflowY: 'hidden',
              }}
            >
              <Routes>
                <Route path="/" element={<PrivateRoutes />}>
                  <Route index element={<Navigate to={findHome(membersStore.connectedAccount)} />} />

                  <Route path={PATHS.tutorial.relative} element={<Tutorial />} />
                  <Route path={PATHS.adoptedTutorial.relative} element={<AdoptedTutorial />} />

                  <Route path={PATHS.adoptions.relative}>
                    <Route index element={<AdoptionsListScreen />} />
                    <Route path={PATHS.adoptionDetails.relative} element={<AdoptionDetailsScreen />} />
                    <Route path={PATHS.adoptionWelcomePack.relative} element={<WelcomePack />} />
                  </Route>

                  <Route path={PATHS.animaux.relative}>
                    <Route index element={<AnimalsListScreen />} />
                    <Route path={PATHS.ajouterAnimal.relative} element={<AddAnimalScreen />} />
                    <Route path={PATHS.animalDetails.relative} element={<AnimalDetailsScreen />} />
                    <Route path={PATHS.ajouterOperation.relative} element={<AddAnimalOperationScreen />} />
                    <Route path={PATHS.animalOperationDetails.relative} element={<OperationDetailsScreen />} />
                    <Route path={PATHS.ajouterObservation.relative} element={<AddAnimalObservationScreen />} />
                    <Route path={PATHS.animalObservationDetails.relative} element={<ObservationDetailsScreen />} />
                    <Route path={PATHS.ajouterDocument.relative} element={<AddAnimalDocumentScreen />} />
                    <Route path={PATHS.animalWelcomePack.relative} element={<WelcomePack />} />
                  </Route>

                  <Route path={PATHS.partenaires.relative}>
                    <Route index />
                    <Route path={PATHS.partenaireDetails.relative} element={<BrandPage />} />
                  </Route>

                  {/* <Route path={PATHS.panier.relative}>
                  <Route index />
                  <Route path={PATHS.recapitulatif.relative} element={<OrderSummary />} />
                </Route> */}

                  <Route path={PATHS.chat.relative}>
                    <Route index element={<ConversationsScreen />} />
                  </Route>

                  <Route path={PATHS.actualites.relative}>
                    <Route index element={<TikTokView />} />
                    <Route path={PATHS.ajouterActualite.relative} element={<SendMyBeReal />} />
                  </Route>

                  <Route path={PATHS['pet-parents'].relative}>
                    <Route index element={<PetParentsListScreen />} />
                  </Route>

                  <Route path={PATHS.services.relative}>
                    <Route index element={<ServicesMapScreen />} />
                    <Route path={PATHS.servicesDetails.relative} element={<ServiceLocationScreen />} />
                  </Route>

                  <Route path={PATHS.profil.relative}>
                    <Route index element={<ProfilScreen />} />
                    <Route path={PATHS.monProfil.relative} element={<ProfileDetailsScreen />} />
                    <Route path={PATHS.formulaire.relative} element={<AdoptionFormScreen />} />
                  </Route>
                </Route>
                <Route path={PATHS.produits.relative} element={<DashboardDisplay />}>
                  <Route index element={<ShoppingTabs />} />
                  <Route path={PATHS.produitDetails.relative} element={<ProductPage />} />
                </Route>
                <Route path={PATHS.panier.relative} element={<DashboardDisplay />}>
                  <Route index element={<ShoppingBasket />} />
                </Route>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/google-callback" element={<AuthRedirect />} />
                <Route path={PATHS.createPassword.absolute} element={<CreatePasswordPage />} />
                <Route path={PATHS.guestAdoption.absolute} element={<GuestAdoptionPage />} />
                <Route path={PATHS.resetPassword.absolute} element={<ResetPasswordPage />} />
                <Route path={PATHS.requestPasswordReset.absolute} element={<RequestResetPasswordPage />} />
                <Route path={PATHS.suivi.absolute} element={<Layout />}>
                  <Route path={PATHS.suiviAnimal.absolute} element={<AnimalDetails />} />
                  <Route path={PATHS.suiviOperationDetails.relative} element={<SuiviOperationDetailsScreen />} />
                  <Route path={PATHS.suiviObservationDetails.relative} element={<SuiviObservationDetailsScreen />} />
                </Route>
              </Routes>
            </Box>

            <GlobalSnackBar />
            <NotificationModal />
          </QueryClientProvider>
        </ThemeProvider>
      </AnalyticsProvider>
    </>
  )
}

// window.onerror = function (message, url, lineNumber, columnNumber, error) {
//   ErrorsClient.notifyError({ message, url, lineNumber, currentUrl: window.location.href, error }).then(() => {
//     return true
//   })
// }

/* window.addEventListener('error', (event) => {
  ErrorsClient.notifyError({
    message: event.message,
    currentUrl: window.location.href,
    error: event.error.toString(),
    stack: event.error.stack,
  }).then(() => {
    return true
  })
}) */
export default App
